import React from 'react';
import Loading from './Loading';
import styles from './TableLoading.module.css';

export interface TableLoadingProps {
  /** Number of skeleton rows to show */
  rows?: number;
  /** Number of columns in the table */
  columns?: number;
  /** Whether the loading state is active */
  loading?: boolean;
  /** Text to display while loading */
  text?: string;
  /** Custom className */
  className?: string;
  /** Children to render when not loading */
  children?: React.ReactNode;
}

const TableLoading: React.FC<TableLoadingProps> = ({
  rows = 5,
  columns = 6,
  loading = true,
  text = 'Loading data...',
  className = '',
  children,
}) => {
  // If not loading and children provided, render children
  if (!loading && children) {
    return <>{children}</>;
  }

  // If not loading and no children, render nothing
  if (!loading) {
    return null;
  }

  const skeletonRows = Array.from({ length: rows }, (_, rowIndex) => (
    <tr key={`skeleton-row-${rowIndex}`} className={styles.skeletonRow}>
      {Array.from({ length: columns }, (_, colIndex) => (
        <td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
          <div className={styles.skeletonCell}></div>
        </td>
      ))}
    </tr>
  ));

  return (
    <div className={`${styles.tableLoading} ${className}`}>
      <div className={styles.loadingOverlay}>
        <Loading type="spinner" size="medium" text={text} />
      </div>
      <table className={styles.skeletonTable}>
        <thead>
          <tr>
            {Array.from({ length: columns }, (_, index) => (
              <th key={`skeleton-header-${index}`}>
                <div className={styles.skeletonHeader}></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{skeletonRows}</tbody>
      </table>
    </div>
  );
};

export default TableLoading;
